import React, { useEffect, useState, useCallback, useMemo, useRef } from 'react'
import { useParams, useNavigate } from 'react-router-dom'
import { AxiosError, isCancel } from 'axios'
import { apiClient } from '../api/client'
import type { Criterion, EditCheck, Field, Form, Job, ProtocolSource, StudyDetail, TabKey, UUID, DeterministicPreview } from '../types'
import EntityTable from './EntityTable'
import StudyOverviewTab from './StudyOverviewTab'
import StudyValidationTab from './StudyValidationTab'
import ConstraintsTab from './ConstraintsTab'
import StudyExportTab from './StudyExportTab'
import CmmReviewPanel, { OdmStandardizationPanel } from './CmmReviewPanel'
import ParserResultTab from './ParserResultTab'
import JobStatusPanel from './JobStatusPanel'
import SourcesTab, { type SourceUpdate } from './study/SourcesTab'
import { packageRanges } from './study/RangesTab'
import { usePersistentState } from '../hooks/usePersistentState'
import { buildBlueprintModel, type FocusRequest } from './study/BuildBlueprint'
import PageHeader from './ui/PageHeader'
import { AlertCircle, FileText, Layers, LoaderCircle, Upload } from 'lucide-react'
import { pluralize } from '../utils/parser'
import { useAuthStore } from '../stores/authStore'

const TAB_KEYS: readonly TabKey[] = ['overview', 'visits', 'ie', 'ranges', 'lab-panels', 'constraints', 'cmm-review', 'odm-standardization', 'validation', 'sources', 'export']
type StudyUploadMode = 'add' | 'replace'
const isTabKey = (value: unknown): value is TabKey => TAB_KEYS.includes(value as TabKey)

function tabFromUrl(): TabKey {
  try {
    const tab = new URLSearchParams(window.location.search).get('tab')
    return isTabKey(tab) ? tab : 'overview'
  } catch {
    return 'overview'
  }
}

/** Keep ?tab= in the address bar so refresh, back and shared links reopen the same tab. */
function writeTabToUrl(tab: TabKey) {
  try {
    const url = new URL(window.location.href)
    if (tab === 'overview') url.searchParams.delete('tab')
    else url.searchParams.set('tab', tab)
    if (url.href !== window.location.href) window.history.replaceState(window.history.state, '', url.href)
  } catch {
    // URL sync is a convenience only.
  }
}

function previewKeyOf(study: StudyDetail): string {
  return `${study.protocol_id ?? ''}|${study.updated_at ?? ''}`
}

function errorMessage(err: unknown, fallback: string): string {
  if (err instanceof AxiosError) {
    const detail = (err.response?.data as { detail?: unknown; message?: unknown } | undefined)
    if (typeof detail?.detail === 'string') return detail.detail
    if (typeof detail?.message === 'string') return detail.message
    if (err.response?.status === 403) return 'You do not have permission to change this study.'
  }
  return err instanceof Error && err.message ? err.message : fallback
}

export default function StudyDetailPage() {
  const { studyId } = useParams<{ studyId: string }>()
  const navigate = useNavigate()
  const [study, setStudy] = useState<StudyDetail | null>(null)
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)
  const [activeTab, setActiveTabState] = useState<TabKey>(tabFromUrl)
  const [searches, setSearches] = useState<Partial<Record<TabKey, string>>>({})
  const [focus, setFocus] = useState<FocusRequest | null>(null)
  const [copied, setCopied] = useState(false)
  const [uploading, setUploading] = useState(false)
  const [uploadProgressMessage, setUploadProgressMessage] = useState<string | null>(null)
  const [uploadModeDialogOpen, setUploadModeDialogOpen] = useState(false)
  const [isExtracting, setIsExtracting] = useState(false)
  const [job, setJob] = useState<Job | null>(null)
  const jobRef = useRef<Job | null>(null)
  if (jobRef.current !== job) {
    jobRef.current = job
  }
  const [uploadError, setUploadError] = useState<string | null>(null)
  const [duplicateFilename, setDuplicateFilename] = useState<string | null>(null)
  const [showConflictDialog, setShowConflictDialog] = useState(false)
  const [pendingFile, setPendingFile] = useState<File | null>(null)
  const [deterministicPreview, setDeterministicPreview] = useState<DeterministicPreview | null>(null)
  const [preparingPreview, setPreparingPreview] = useState(false)
  const [previewError, setPreviewError] = useState<string | null>(null)
  const [sources, setSources] = useState<ProtocolSource[] | null>(null)
  const [sourcesError, setSourcesError] = useState<string | null>(null)
  const uploadSessionRef = useRef(0)
  const uploadInputRef = useRef<HTMLInputElement>(null)
  const uploadModeRef = useRef<StudyUploadMode>('add')
  const canUploadProtocols = useAuthStore((state) => state.user?.role !== 'Viewer')

  // Preparation is an in-request server computation, not a durable job, so the
  // "Preparing your protocol" status only lives in this component. Persist the
  // moment it started (keyed to the exact preparation) so the elapsed clock
  // stays continuous when the Documents tab unmounts — switching tabs or
  // navigating away and back no longer restarts it from 0:00.
  const [prepStart, setPrepStart] = usePersistentState<{ key: string; at: number } | null>(
    `pc.prep.start.${studyId ?? 'none'}`,
    null,
  )
  const prepKey = study ? previewKeyOf(study) : ''
  useEffect(() => {
    // Set-only: a remount re-enters "preparing" with the same key, so keep the
    // stored start instead of overwriting it; a genuinely new preparation has
    // a new key and gets a fresh start. Never clear here — clearing on the
    // transient initial `false` after a remount would reset the clock.
    if (preparingPreview && prepKey) {
      setPrepStart((current) => (current && current.key === prepKey ? current : { key: prepKey, at: Date.now() }))
    }
  }, [preparingPreview, prepKey, setPrepStart])
  const preparingStartedAt =
    preparingPreview && prepStart && prepStart.key === prepKey
      ? new Date(prepStart.at).toISOString()
      : null

  const search = searches[activeTab] ?? ''
  const setSearch = useCallback((value: string) => setSearches((current) => ({ ...current, [activeTab]: value })), [activeTab])

  const setActiveTab = useCallback((tab: TabKey) => {
    setActiveTabState(tab)
    writeTabToUrl(tab)
  }, [])

  // Cross-tab links (e.g. a criterion chip in the blueprint) open the target
  // tab, clear its search so the item is not filtered out, and focus it.
  const navigateTo = useCallback((tab: TabKey, anchor?: string) => {
    setSearches((current) => ({ ...current, [tab]: '' }))
    setActiveTab(tab)
    setFocus(anchor ? { anchor, seq: Date.now() } : null)
  }, [setActiveTab])

  const fetchStudy = useCallback(async (): Promise<StudyDetail | null> => {
    if (!studyId) return null
    setLoading((current) => current && !study)
    setError(null)
    try {
      const data = await apiClient.get<StudyDetail>(`/studies/${studyId}/detail`)
      setStudy(data)
      return data
    } catch (err: unknown) {
      setError(err instanceof Error ? err.message : 'Failed to load study')
      return null
    } finally {
      setLoading(false)
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [studyId])

  const fetchSources = useCallback(async () => {
    if (!studyId) return
    try {
      const data = await apiClient.get<ProtocolSource[]>(`/studies/${studyId}/protocol-sources`)
      setSources(Array.isArray(data) ? data : [])
      setSourcesError(null)
    } catch (err: unknown) {
      setSources([])
      setSourcesError(errorMessage(err, 'The document list could not be loaded.'))
    }
  }, [studyId])

  useEffect(() => {
    fetchStudy()
    fetchSources()
  }, [fetchStudy, fetchSources])

  // The combined preview is keyed on the study's protocol and update time;
  // any change that affects the build (upload, include/exclude) bumps them.
  const previewKeyRef = useRef<string | null>(null)
  useEffect(() => {
    if (!studyId || !study) return
    previewKeyRef.current = previewKeyOf(study)
    const session = ++uploadSessionRef.current
    loadDeterministicPreview(session)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [studyId, study?.protocol_id, study?.updated_at])

  // Warn before leaving while a protocol upload is in flight.
  useEffect(() => {
    if (!uploading) return
    const handler = (event: BeforeUnloadEvent) => {
      event.preventDefault()
      event.returnValue = ''
    }
    window.addEventListener('beforeunload', handler)
    return () => window.removeEventListener('beforeunload', handler)
  }, [uploading])

  const deleteRow = (endpoint: string) => async () => {
    if (!studyId) return
    await apiClient.delete(`/studies/${studyId}${endpoint}`)
    fetchStudy()
  }

  const addEntity = async (endpoint: string, data: Record<string, unknown>) => {
    if (!studyId) return
    await apiClient.post(`/studies/${studyId}${endpoint}`, data)
    fetchStudy()
  }

  const loadDeterministicPreview = async (session: number) => {
    if (!studyId) return
    setPreparingPreview(true)
    setPreviewError(null)
    try {
      // No client timeout: a first-time preview parses and builds the whole
      // protocol server-side, which can take minutes for long documents.
      const preview = await apiClient.get<DeterministicPreview>(`/studies/${studyId}/deterministic-preview`, { timeout: 0 })
      if (session === uploadSessionRef.current) {
        // Do not treat a legacy study-detail response as a preview payload.
        // The preview renderer relies on these arrays being present.
        if (preview && Array.isArray(preview.sections) && typeof preview.ai_payload === 'string') {
          setDeterministicPreview(preview)
        } else {
          setDeterministicPreview(null)
        }
      }
    } catch (err: unknown) {
      if (session === uploadSessionRef.current) {
        const status = err instanceof AxiosError ? err.response?.status : undefined
        // 404 means no document is linked yet; that is an empty state, not an error.
        if (status === 404) setDeterministicPreview(null)
        else if (status === 410) {
          setDeterministicPreview(null)
          setPreviewError('The study record exists, but its uploaded protocol file is unavailable. Re-upload the PDF after configuring persistent storage.')
        }
        else setPreviewError(errorMessage(err, 'The protocol uploaded, but preparation could not be displayed.'))
      }
    } finally {
      if (session === uploadSessionRef.current) {
        setPreparingPreview(false)
      }
    }
  }

  const refreshPreview = () => {
    const session = ++uploadSessionRef.current
    loadDeterministicPreview(session)
  }

  const discoverLatestJobForStudy = useCallback(async () => {
    if (!studyId) return
    try {
      const jobsResponse = await apiClient.get<Job[] | { items: Job[] }>(`/jobs/recent?limit=50`)
      const jobs = Array.isArray(jobsResponse) ? jobsResponse : (jobsResponse?.items || [])
      const jobsForStudy = jobs.filter((j) => j.study_id === studyId)
      const runningJob = jobsForStudy.find((j) => ['pending', 'running', 'waiting_for_rate_limit'].includes(j.status))
      if (runningJob) {
        setJob(runningJob)
        setIsExtracting(true)
      } else if (jobsForStudy.length > 0 && !jobRef.current) {
        // Surface the most recent terminal job for context without starting a poll loop.
        setJob(jobsForStudy[0])
      }
    } catch {
      // Best-effort; don't block the study page from loading.
    }
  }, [studyId])

  useEffect(() => {
    if (!studyId) return
    // Allow a short delay for the initial study fetch to avoid duplicate requests.
    const id = setTimeout(() => discoverLatestJobForStudy(), 500)
    return () => clearTimeout(id)
  }, [studyId, discoverLatestJobForStudy])

  const resetUploadState = () => {
    setJob(null)
    setIsExtracting(false)
    setUploadError(null)
    setDuplicateFilename(null)
    setShowConflictDialog(false)
    setPendingFile(null)
    setPreviewError(null)
  }

  const doUpload = async (file: File, onConflict: 'block' | 'replace' | 'merge', options?: { session?: number; resetSession?: boolean; refreshAfter?: boolean; progressLabel?: string; studyUploadMode?: StudyUploadMode }): Promise<boolean> => {
    if (!file || !studyId) return false
    const session = options?.session ?? uploadSessionRef.current + 1
    if (options?.resetSession !== false) uploadSessionRef.current = session
    setUploading(true)
    setUploadProgressMessage(options?.progressLabel || `Uploading ${file.name}`)
    // A new protocol upload starts a new session. Do not keep displaying the
    // previous completed, failed, or canceled job while it is being replaced.
    if (options?.resetSession !== false) resetUploadState()
    try {
      const data = await apiClient.uploadFile<{
        id: string
        job_id?: string
        status?: string
        message?: string
        merge_mode?: boolean
      }>('/protocols', file, undefined, undefined, {
        study_id: studyId,
        on_conflict: onConflict,
        study_upload_mode: options?.studyUploadMode ?? 'add',
        auto_extract: 'false',
      })
      if (data.job_id && !data.merge_mode) {
        setIsExtracting(true)
        setJob(await apiClient.get<Job>(`/jobs/${data.job_id!}`))
      }
      if (options?.refreshAfter !== false) await Promise.all([fetchStudy(), fetchSources()])
      return true
    } catch (err: unknown) {
      if (isCancel(err)) {
        return false
      }
      if (err instanceof AxiosError && err.response?.status === 409) {
        const errData = err.response.data as { error?: string; message?: string; details?: { existing_protocol_id?: string; filename?: string } } | undefined
        const existingId = errData?.details?.existing_protocol_id ?? null
        if (existingId) {
          setDuplicateFilename(errData?.details?.filename ?? null)
          setPendingFile(file)
          setShowConflictDialog(true)
        } else {
          setUploadError(errData?.message ?? 'A duplicate protocol was detected, but the existing ID could not be found.')
        }
      } else if (err instanceof AxiosError && !err.response) {
        setUploadError('Network error. The upload may still be processing; check the Jobs page.')
      } else {
        setUploadError(errorMessage(err, `Failed to upload ${file.name}`))
      }
      return false
    } finally {
      if (options?.refreshAfter !== false) {
        setUploading(false)
        setUploadProgressMessage(null)
      }
    }
  }

  const uploadFiles = async (files: File[], mode: StudyUploadMode = 'add') => {
    if (!files.length || !studyId || uploading) return
    const invalidFiles = files.filter((file) => !file.name.toLowerCase().endsWith('.pdf'))
    if (invalidFiles.length) {
      setUploadError(`Only PDF files are supported. Remove: ${invalidFiles.map((file) => file.name).join(', ')}`)
      return
    }
    const session = uploadSessionRef.current + 1
    uploadSessionRef.current = session
    setUploading(true)
    resetUploadState()
    try {
      for (let index = 0; index < files.length; index += 1) {
        const file = files[index]
        const uploaded = await doUpload(file, mode === 'replace' ? 'replace' : 'merge', {
          session,
          resetSession: false,
          refreshAfter: false,
          studyUploadMode: index === 0 ? mode : 'add',
          progressLabel: files.length > 1 ? `Uploading ${index + 1} of ${files.length}: ${file.name}` : `Uploading ${file.name}`,
        })
        if (!uploaded) break
      }
      const [latest] = await Promise.all([fetchStudy(), fetchSources()])
      // When the study changed, the preview effect reloads it; only refresh
      // here when nothing changed so the heavy preview is never built twice.
      if (latest && previewKeyOf(latest) === previewKeyRef.current && session === uploadSessionRef.current) {
        await loadDeterministicPreview(session)
      }
    } finally {
      setUploading(false)
      setUploadProgressMessage(null)
    }
  }

  const beginUpload = (mode: StudyUploadMode) => {
    uploadModeRef.current = mode
    setUploadModeDialogOpen(false)
    // Let the modal close before opening the native picker. This avoids the
    // picker being suppressed by browsers while a dialog is being removed.
    window.setTimeout(() => uploadInputRef.current?.click(), 0)
  }

  const handleUploadInput = (e: React.ChangeEvent<HTMLInputElement>) => {
    const files = Array.from(e.target.files || [])
    // Reset immediately so the same file can be selected again after any
    // terminal or interrupted upload state.
    e.currentTarget.value = ''
    uploadFiles(files, uploadModeRef.current)
  }

  const updateSource = async (id: UUID, update: SourceUpdate) => {
    if (!studyId) return
    try {
      const updated = await apiClient.patch<ProtocolSource>(`/studies/${studyId}/protocol-sources/${id}`, update)
      setSources((current) => (current || []).map((source) => (source.id === id ? { ...source, ...updated } : source)))
    } catch (err) {
      throw new Error(errorMessage(err, 'The change could not be saved.'))
    }
    // The study's updated_at changes, which reloads the combined preview.
    await Promise.all([fetchStudy(), fetchSources()])
  }

  const handleReplace = async () => {
    if (!pendingFile) return
    setShowConflictDialog(false)
    await doUpload(pendingFile, 'replace')
  }

  const handleMerge = async () => {
    if (!pendingFile) return
    setShowConflictDialog(false)
    await doUpload(pendingFile, 'merge')
  }

  const parserResult = deterministicPreview?.clin_spark_study_package
  const hasParserResult = Boolean(parserResult && parserResult.status !== 'FAILED')
  const cmm = (parserResult?.study_build_design?.CMM || {}) as Record<string, unknown>
  const activeSources = (sources || []).filter((source) => source.is_active)

  const counts = useMemo(() => {
    if (!study) return null
    if (hasParserResult && parserResult) {
      const ranges = packageRanges(parserResult)
      return {
        // Same grouping as the blueprint (timepoints fold into their parent visit).
        visits: buildBlueprintModel(parserResult).blueprint.length,
        ranges: ranges.length,
        rangesReview: ranges.filter((range) => range.human_review_required).length,
        labs: Array.isArray(cmm.laboratory_dictionary) ? (cmm.laboratory_dictionary as unknown[]).length : parserResult.lab_panels.length,
        ie: parserResult.eligibility.inclusion.length + parserResult.eligibility.exclusion.length,
        ieReview: [...parserResult.eligibility.inclusion, ...parserResult.eligibility.exclusion].filter((item) => (item.text_variants || []).length > 1).length,
        constraints: 0,
      }
    }
    return {
      visits: study.visits.length,
      ranges: study.ranges.length,
      rangesReview: 0,
      labs: study.lab_panels.length,
      ie: study.criteria.length,
      ieReview: 0,
      constraints: Array.isArray(study.metadata?.constraints) ? (study.metadata?.constraints as unknown[]).length : 0,
    }
  }, [study, hasParserResult, parserResult, cmm])

  if (loading) {
    return (
      <div className="animate-pulse space-y-4">
        <div className="h-8 bg-gray-200 dark:bg-gray-700 rounded w-1/3"></div>
        <div className="h-64 bg-gray-200 dark:bg-gray-700 rounded"></div>
      </div>
    )
  }

  if (error || !study || !counts) {
    return (
      <div className="p-4 bg-red-50 dark:bg-red-900/20 text-red-700 dark:text-red-300 rounded-lg">
        {error || 'Study not found'}
        <button
          onClick={() => navigate('/studies')}
          className="ml-4 px-3 py-1 text-sm bg-gray-200 dark:bg-gray-700 rounded hover:bg-gray-300 dark:hover:bg-gray-600"
        >
          Back to Studies
        </button>
      </div>
    )
  }

  const tabs: { key: TabKey; label: string; count?: number; attention?: number }[] = [
    { key: 'overview', label: 'Overview' },
    { key: 'visits', label: 'ClinSpark Build Blueprint', count: counts.visits },
    { key: 'ie', label: 'I/E', count: counts.ie, attention: counts.ieReview },
    { key: 'ranges', label: 'Ranges', count: counts.ranges, attention: counts.rangesReview },
    { key: 'lab-panels', label: 'Lab Panels', count: counts.labs },
    { key: 'constraints', label: 'Constraints', count: hasParserResult ? undefined : counts.constraints },
    { key: 'cmm-review', label: 'CMM Review' },
    { key: 'odm-standardization', label: 'ODM Standardization' },
    { key: 'validation', label: 'Validation' },
    { key: 'sources', label: 'Documents', count: sources ? activeSources.length : undefined },
    { key: 'export', label: 'Export' },
  ]

  const onTabKeyDown = (event: React.KeyboardEvent<HTMLButtonElement>, index: number) => {
    const delta = event.key === 'ArrowRight' ? 1 : event.key === 'ArrowLeft' ? -1 : 0
    const target = event.key === 'Home' ? 0 : event.key === 'End' ? tabs.length - 1 : delta ? (index + delta + tabs.length) % tabs.length : -1
    if (target < 0) return
    event.preventDefault()
    setActiveTab(tabs[target].key)
    const button = event.currentTarget.parentElement?.querySelectorAll<HTMLButtonElement>('[role="tab"]')[target]
    button?.focus()
  }

  const parserTab = hasParserResult && ['visits', 'ie', 'ranges', 'lab-panels', 'constraints', 'validation'].includes(activeTab)
  const showActivity = uploading || preparingPreview || Boolean(previewError) || Boolean(uploadError)

  return (
    <div className="space-y-5">
      <PageHeader
        title={study.study_name}
        subtitle={
          <span className="flex flex-wrap items-center gap-2">
            {study.study_code || 'No code'}
            <span className="text-gray-300 dark:text-gray-600">·</span>
            {study.phase || 'No phase'}
          </span>
        }
        breadcrumbs={[{ label: 'Studies', to: '/studies' }, { label: study.study_name }]}
      >
        {canUploadProtocols && <button type="button" onClick={() => setUploadModeDialogOpen(true)} disabled={uploading} className={`btn-primary ${uploading ? 'cursor-wait opacity-70' : 'cursor-pointer'}`}>
          {uploading ? <LoaderCircle className="mr-1.5 h-4 w-4 animate-spin" aria-hidden={true} /> : <Upload className="mr-1.5 h-4 w-4" aria-hidden={true} />}
          {uploading ? 'Uploading…' : 'Upload protocol'}
        </button>}
        {canUploadProtocols && <input
          ref={uploadInputRef}
          type="file"
          accept=".pdf,application/pdf"
          multiple
          className="hidden"
          onChange={handleUploadInput}
          disabled={uploading}
        />}
      </PageHeader>

      {uploadModeDialogOpen && canUploadProtocols && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4" role="dialog" aria-modal="true" aria-labelledby="upload-mode-title">
          <div className="w-full max-w-lg rounded-xl bg-white p-6 shadow-xl dark:bg-gray-800">
            <h2 id="upload-mode-title" className="text-lg font-semibold text-gray-900 dark:text-white">Upload protocol to this study</h2>
            <p className="mt-2 text-sm text-gray-600 dark:text-gray-300">Choose how the selected PDF files should affect the current study.</p>
            <div className="mt-5 grid gap-3 sm:grid-cols-2">
              <button type="button" onClick={() => beginUpload('add')} className="rounded-lg border border-gray-200 p-4 text-left hover:border-primary-400 hover:bg-primary-50 dark:border-gray-700 dark:hover:bg-primary-950/30">
                <span className="block font-semibold text-gray-900 dark:text-white">Add to study</span>
                <span className="mt-1 block text-sm text-gray-600 dark:text-gray-300">Keep the existing protocol sources and append the selected documents.</span>
              </button>
              <button type="button" onClick={() => beginUpload('replace')} className="rounded-lg border border-red-200 p-4 text-left hover:border-red-400 hover:bg-red-50 dark:border-red-900 dark:hover:bg-red-950/30">
                <span className="block font-semibold text-gray-900 dark:text-white">Replace study protocols</span>
                <span className="mt-1 block text-sm text-gray-600 dark:text-gray-300">Remove the current generated study data and source links, then process the selected documents.</span>
              </button>
            </div>
            <div className="mt-5 flex justify-end">
              <button type="button" onClick={() => setUploadModeDialogOpen(false)} className="btn-secondary">Cancel</button>
            </div>
          </div>
        </div>
      )}

      <StudyWorkspaceSummary study={study} documentCount={sources ? activeSources.length : null} readiness={hasParserResult ? parserResult?.quality_control?.build_readiness_score ?? null : null} onOpenDocuments={() => setActiveTab('sources')} />

      {showActivity && !showConflictDialog && (
        <ActivityBanner
          uploading={uploading}
          uploadMessage={uploadProgressMessage}
          preparing={preparingPreview}
          error={uploadError || previewError}
          documentCount={activeSources.length}
          onDetails={activeTab === 'sources' ? undefined : () => setActiveTab('sources')}
          onRetry={previewError && !uploadError ? refreshPreview : undefined}
          onDismiss={uploadError ? () => setUploadError(null) : undefined}
        />
      )}

      {showConflictDialog && (
        <div role="alertdialog" aria-labelledby="conflict-title" className="p-4 bg-yellow-50 dark:bg-yellow-900/20 border border-yellow-200 dark:border-yellow-800 text-yellow-800 dark:text-yellow-300 rounded-lg space-y-3">
          <p id="conflict-title" className="font-medium">This protocol already exists</p>
          <p className="text-sm">
            A protocol with the same content already exists
            {duplicateFilename ? ` (${duplicateFilename})` : ''}.
            What would you like to do?
          </p>
          <div className="flex flex-col gap-2 sm:flex-row">
            <button
              onClick={handleMerge}
              className="inline-flex items-center justify-center px-4 py-2 bg-blue-600 hover:bg-blue-700 text-white text-sm font-medium rounded-lg transition-colors"
            >
              Add to this study
            </button>
            <button
              onClick={handleReplace}
              className="inline-flex items-center justify-center px-4 py-2 bg-red-600 hover:bg-red-700 text-white text-sm font-medium rounded-lg transition-colors"
            >
              Replace and re-extract
            </button>
            <button
              onClick={() => {
                setShowConflictDialog(false)
                setPendingFile(null)
              }}
              className="inline-flex items-center justify-center px-4 py-2 bg-gray-200 dark:bg-gray-700 hover:bg-gray-300 dark:hover:bg-gray-600 text-gray-800 dark:text-gray-200 text-sm font-medium rounded-lg transition-colors"
            >
              Cancel
            </button>
          </div>
        </div>
      )}

      {job?.id && (
        <JobStatusPanel
          jobId={job.id}
          fileName={job.filename || study?.study_name || undefined}
          compact={!isExtracting && job.status === 'completed'}
          onStatusChange={(updatedJob) => {
            const current = jobRef.current
            if (current && updatedJob.updated_at <= current.updated_at) {
              return
            }
            setJob(updatedJob)
            if (updatedJob.status === 'completed' || updatedJob.status === 'needs_review') {
              fetchStudy()
            }
          }}
        />
      )}

      <div className="border-b border-gray-200 dark:border-gray-700">
        <div role="tablist" aria-label="Study sections" className="-mb-px flex gap-1 overflow-x-auto">
          {tabs.map((tab, index) => {
            const selected = activeTab === tab.key
            return (
              <button
                key={tab.key}
                id={`study-tab-${tab.key}`}
                role="tab"
                type="button"
                aria-selected={selected}
                aria-controls="study-tabpanel"
                tabIndex={selected ? 0 : -1}
                onClick={() => setActiveTab(tab.key)}
                onKeyDown={(event) => onTabKeyDown(event, index)}
                className={`inline-flex items-center gap-1.5 whitespace-nowrap border-b-2 px-3 py-2 text-sm font-medium transition-colors ${
                  selected
                    ? 'border-primary-500 text-primary-600 dark:text-primary-400'
                    : 'border-transparent text-gray-500 hover:border-gray-300 hover:text-gray-700 dark:text-gray-400 dark:hover:text-gray-200'
                }`}
              >
                {tab.label}
                {tab.count !== undefined && (
                  <span className={`rounded-full px-1.5 py-0.5 text-2xs font-semibold tabular-nums ${selected ? 'bg-primary-100 text-primary-700 dark:bg-primary-900/50 dark:text-primary-200' : 'bg-gray-100 text-gray-600 dark:bg-gray-700 dark:text-gray-300'}`}>
                    {tab.count}
                  </span>
                )}
                {Boolean(tab.attention) && <span className="h-1.5 w-1.5 rounded-full bg-amber-500" title={`${tab.attention} need review`} aria-label={`${tab.attention} need review`} />}
              </button>
            )
          })}
        </div>
      </div>

      <div id="study-tabpanel" role="tabpanel" aria-labelledby={`study-tab-${activeTab}`} className="min-h-[400px]">
        {activeTab === 'cmm-review' && (
          <CmmReviewPanel preview={deterministicPreview} loading={preparingPreview} />
        )}
        {activeTab === 'odm-standardization' && (
          <OdmStandardizationPanel preview={deterministicPreview} loading={preparingPreview} />
        )}
        {activeTab === 'overview' && (
          <StudyOverviewTab study={study} parserMetadata={parserResult?.study_metadata} parserData={hasParserResult ? parserResult : undefined} onNavigate={setActiveTab} documentCount={sources ? activeSources.length : undefined} />
        )}
        {activeTab === 'sources' && (
          <SourcesTab
            sources={sources}
            sourcesError={sourcesError}
            preview={deterministicPreview}
            preparing={preparingPreview}
            preparingStartedAt={preparingStartedAt}
            previewError={previewError}
            uploading={uploading}
            uploadProgress={uploadProgressMessage}
            canUpload={canUploadProtocols}
            onUpload={uploadFiles}
            onUpdateSource={updateSource}
            onRetryPreview={refreshPreview}
          />
        )}

        {parserTab && (
          <ParserResultTab tab={activeTab} data={parserResult!} search={search} onSearchChange={setSearch} onNavigate={navigateTo} focus={focus} />
        )}

        {!hasParserResult && preparingPreview && ['visits', 'ie', 'ranges', 'lab-panels'].includes(activeTab) && (
          <div className="mb-4 flex items-center gap-2 rounded-lg border border-primary-200 bg-primary-50/60 px-4 py-3 text-sm text-primary-900 dark:border-primary-900 dark:bg-primary-950/30 dark:text-primary-100" role="status">
            <LoaderCircle className="h-4 w-4 animate-spin" aria-hidden={true} />
            Preparing protocol documents. The extracted build will replace this view when ready.
          </div>
        )}

        {activeTab === 'visits' && !hasParserResult && (
          <EntityTable
            title="Visits"
            columns={['Name', 'Code', 'Day', 'Window Min', 'Window Max', 'Order']}
            rows={study.visits.filter((v) =>
              [v.visit_name, v.visit_code].join(' ').toLowerCase().includes(search.toLowerCase())
            )}
            addFields={[
              { key: 'visit_name', label: 'Name', required: true },
              { key: 'visit_code', label: 'Code' },
              { key: 'day_number', label: 'Day', type: 'number' },
              { key: 'window_min', label: 'Window Min', type: 'number' },
              { key: 'window_max', label: 'Window Max', type: 'number' },
              { key: 'sequence_order', label: 'Order', type: 'number' },
            ]}
            onAdd={(data) => addEntity('/visits', data)}
            renderRow={(visit) => (
              <tr key={visit.id} className="hover:bg-gray-50 dark:hover:bg-gray-700/50">
                <td className="px-4 py-3 text-sm text-gray-900 dark:text-white">{visit.visit_name}</td>
                <td className="px-4 py-3 text-sm text-gray-500 dark:text-gray-400">{visit.visit_code || '—'}</td>
                <td className="px-4 py-3 text-sm text-gray-500 dark:text-gray-400">{visit.day_number ?? '—'}</td>
                <td className="px-4 py-3 text-sm text-gray-500 dark:text-gray-400">{visit.window_min ?? '—'}</td>
                <td className="px-4 py-3 text-sm text-gray-500 dark:text-gray-400">{visit.window_max ?? '—'}</td>
                <td className="px-4 py-3 text-sm text-gray-500 dark:text-gray-400">{visit.sequence_order}</td>
                <td className="px-4 py-3 text-sm">
                  <button onClick={deleteRow(`/visits/${visit.id}`)} className="text-red-600 dark:text-red-400 hover:underline text-xs">
                    Delete
                  </button>
                </td>
              </tr>
            )}
            extraActions={
              <div className="flex items-center gap-2">
                <input
                  type="text"
                  value={search}
                  onChange={(e) => setSearch(e.target.value)}
                  placeholder="Search visits..."
                  className="px-3 py-1.5 text-sm border rounded dark:bg-gray-800 dark:border-gray-600 dark:text-white"
                />
                <button
                  onClick={async () => {
                    const list = study.visits.map((v) => v.visit_name).join('\n')
                    await navigator.clipboard?.writeText(list)
                    setCopied(true)
                    setTimeout(() => setCopied(false), 2000)
                  }}
                  className="px-3 py-1.5 text-sm bg-gray-100 dark:bg-gray-700 text-gray-700 dark:text-gray-200 rounded hover:bg-gray-200 dark:hover:bg-gray-600"
                >
                  {copied ? 'Copied!' : 'Copy visits'}
                </button>
              </div>
            }
          />
        )}

        {activeTab === 'ranges' && !hasParserResult && (
          <EntityTable
            title="Ranges"
            columns={['Item', 'Category', 'Min', 'Max', 'Unit', 'Field']}
            rows={study.ranges.filter((r) =>
              [r.item_name, r.category, r.field_name, r.unit].join(' ').toLowerCase().includes(search.toLowerCase())
            )}
            addFields={[
              { key: 'item_name', label: 'Item', required: true },
              { key: 'category', label: 'Category' },
              { key: 'min_value', label: 'Min' },
              { key: 'max_value', label: 'Max' },
              { key: 'unit', label: 'Unit' },
              { key: 'field_name', label: 'Field' },
            ]}
            onAdd={(data) => addEntity('/ranges', data)}
            renderRow={(rng) => (
              <tr key={rng.id} className="hover:bg-gray-50 dark:hover:bg-gray-700/50">
                <td className="px-4 py-3 text-sm text-gray-900 dark:text-white">{rng.item_name}</td>
                <td className="px-4 py-3 text-sm text-gray-500 dark:text-gray-400">{rng.category || '—'}</td>
                <td className="px-4 py-3 text-sm text-gray-500 dark:text-gray-400">{rng.min_value || '—'}</td>
                <td className="px-4 py-3 text-sm text-gray-500 dark:text-gray-400">{rng.max_value || '—'}</td>
                <td className="px-4 py-3 text-sm text-gray-500 dark:text-gray-400">{rng.unit || '—'}</td>
                <td className="px-4 py-3 text-sm text-gray-500 dark:text-gray-400">{rng.field_name || '—'}</td>
                <td className="px-4 py-3 text-sm">
                  <button onClick={deleteRow(`/ranges/${rng.id}`)} className="text-red-600 dark:text-red-400 hover:underline text-xs">
                    Delete
                  </button>
                </td>
              </tr>
            )}
            extraActions={
              <input
                type="text"
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                placeholder="Search ranges..."
                className="px-3 py-1.5 text-sm border rounded dark:bg-gray-800 dark:border-gray-600 dark:text-white"
              />
            }
          />
        )}

        {activeTab === 'constraints' && !hasParserResult && studyId && (
          <ConstraintsTab studyId={studyId as UUID} />
        )}

        {activeTab === 'lab-panels' && !hasParserResult && (
          <EntityTable
            title="Lab Panels"
            columns={['Name', 'Code', 'Specimen Type', 'Tests']}
            rows={study.lab_panels.filter((lp) =>
              [lp.panel_name, lp.panel_code, lp.specimen_type].join(' ').toLowerCase().includes(search.toLowerCase())
            )}
            addFields={[
              { key: 'panel_name', label: 'Name', required: true },
              { key: 'panel_code', label: 'Code' },
              { key: 'specimen_type', label: 'Specimen Type' },
              { key: 'processing_instructions', label: 'Instructions' },
            ]}
            onAdd={(data) => addEntity('/lab-panels', data)}
            renderRow={(lp) => (
              <tr key={lp.id} className="hover:bg-gray-50 dark:hover:bg-gray-700/50">
                <td className="px-4 py-3 text-sm text-gray-900 dark:text-white">{lp.panel_name}</td>
                <td className="px-4 py-3 text-sm text-gray-500 dark:text-gray-400">{lp.panel_code || '—'}</td>
                <td className="px-4 py-3 text-sm text-gray-500 dark:text-gray-400">{lp.specimen_type || '—'}</td>
                <td className="px-4 py-3 text-sm text-gray-500 dark:text-gray-400">{lp.tests?.length ?? 0}</td>
                <td className="px-4 py-3 text-sm">
                  <button onClick={deleteRow(`/lab-panels/${lp.id}`)} className="text-red-600 dark:text-red-400 hover:underline text-xs">
                    Delete
                  </button>
                </td>
              </tr>
            )}
            extraActions={
              <input
                type="text"
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                placeholder="Search panels..."
                className="px-3 py-1.5 text-sm border rounded dark:bg-gray-800 dark:border-gray-600 dark:text-white"
              />
            }
          />
        )}

        {activeTab === 'ie' && !hasParserResult && (
          <CriteriaTab study={study} search={search} onSearchChange={setSearch} />
        )}

        {activeTab === 'validation' && !hasParserResult && studyId && (
          <StudyValidationTab studyId={studyId as UUID} />
        )}

        {activeTab === 'export' && studyId && (
          <StudyExportTab studyId={studyId as UUID} deterministicPreview={deterministicPreview} />
        )}
      </div>
    </div>
  )
}

function StudyWorkspaceSummary({ study, documentCount, readiness, onOpenDocuments }: {
  study: StudyDetail
  documentCount: number | null
  readiness: number | null
  onOpenDocuments: () => void
}) {
  const summary = (study.metadata as import('../types').StudyMetadata | null)?.extraction_summary
  const lowConfidenceCritical = summary?.low_confidence_critical ?? 0

  return (
    <div className="panel flex flex-wrap items-center gap-x-6 gap-y-3 px-4 py-3 text-sm">
      <button type="button" onClick={onOpenDocuments} className="inline-flex items-center gap-2 rounded text-gray-700 hover:text-primary-700 dark:text-gray-200 dark:hover:text-primary-300">
        <FileText className="h-4 w-4 text-gray-400" aria-hidden={true} />
        {documentCount === null ? 'Documents' : documentCount === 0 ? 'No documents yet' : pluralize(documentCount, 'protocol document')}
      </button>
      {readiness !== null && (
        <span className="inline-flex items-center gap-2 text-gray-700 dark:text-gray-200">
          <Layers className="h-4 w-4 text-gray-400" aria-hidden={true} />
          Build readiness <strong className="tabular-nums">{Math.round(readiness)}%</strong>
        </span>
      )}
      {lowConfidenceCritical > 0 && (
        <span className="text-red-600 dark:text-red-400">
          {lowConfidenceCritical} low-confidence critical {lowConfidenceCritical === 1 ? 'item needs' : 'items need'} review
        </span>
      )}
    </div>
  )
}

function ActivityBanner({ uploading, uploadMessage, preparing, error, documentCount, onDetails, onRetry, onDismiss }: {
  uploading: boolean
  uploadMessage: string | null
  preparing: boolean
  error: string | null
  documentCount: number
  onDetails?: () => void
  onRetry?: () => void
  onDismiss?: () => void
}) {
  const busy = uploading || preparing
  const message = error && !busy
    ? error
    : uploading
      ? uploadMessage || 'Uploading protocol…'
      : `Preparing ${documentCount > 1 ? `${documentCount} protocol documents` : 'the protocol'}. Long protocols can take a minute; you can keep working.`
  return (
    <div
      role={error && !busy ? 'alert' : 'status'}
      className={`flex flex-wrap items-center gap-3 rounded-lg border px-4 py-2.5 text-sm ${error && !busy ? 'border-red-200 bg-red-50 text-red-800 dark:border-red-900 dark:bg-red-950/30 dark:text-red-200' : 'border-primary-200 bg-primary-50/70 text-primary-900 dark:border-primary-900 dark:bg-primary-950/30 dark:text-primary-100'}`}
    >
      {busy ? <LoaderCircle className="h-4 w-4 shrink-0 animate-spin" aria-hidden={true} /> : <AlertCircle className="h-4 w-4 shrink-0" aria-hidden={true} />}
      <span className="min-w-0 flex-1">{message}</span>
      <span className="flex items-center gap-3">
        {onRetry && !busy && <button type="button" onClick={onRetry} className="font-medium underline-offset-2 hover:underline">Retry</button>}
        {onDetails && <button type="button" onClick={onDetails} className="font-medium underline-offset-2 hover:underline">View details</button>}
        {onDismiss && !busy && <button type="button" onClick={onDismiss} className="font-medium underline-offset-2 hover:underline">Dismiss</button>}
      </span>
    </div>
  )
}

export function FormDetailPanel({
  form,
  editChecks,
  onClear,
}: {
  form: Form | undefined
  editChecks: EditCheck[]
  onClear: () => void
}) {
  if (!form) return null
  const groups = form.item_groups ?? []
  const ungrouped = form.fields ?? []
  const hasData = groups.length > 0 || ungrouped.length > 0

  return (
    <div className="bg-white dark:bg-gray-800 rounded-lg border border-gray-200 dark:border-gray-700 p-4 space-y-4">
      <div className="flex items-start justify-between">
        <div>
          <h4 className="text-base font-semibold text-gray-900 dark:text-white">{form.form_name}</h4>
          <p className="text-sm text-gray-500 dark:text-gray-400">
            {form.form_code ? `Code: ${form.form_code}` : 'No form code'} • {groups.length} item group(s) • {groups.reduce((sum, g) => sum + (g.fields?.length ?? 0), 0) + ungrouped.length} field(s)
          </p>
          {form.description && <p className="text-sm text-gray-600 dark:text-gray-300 mt-1">{form.description}</p>}
          {form.source_evidence && (
            <p className="text-xs text-gray-500 dark:text-gray-400 mt-1">Source: {form.source_evidence}</p>
          )}
          {form.confidence !== null && form.confidence !== undefined && (
            <p className="text-xs text-gray-500 dark:text-gray-400">Confidence: {(form.confidence * 100).toFixed(0)}%</p>
          )}
        </div>
        <button onClick={onClear} className="text-sm text-gray-500 hover:text-gray-700 dark:hover:text-gray-300">
          Close
        </button>
      </div>

      {!hasData && (
        <div className="bg-yellow-50 dark:bg-yellow-900/20 border border-yellow-200 dark:border-yellow-800 p-3 rounded text-sm text-yellow-800 dark:text-yellow-200">
          No item groups or fields mapped to this form.
        </div>
      )}

      {groups.map((group) => (
        <div key={group.id} className="border border-gray-200 dark:border-gray-700 rounded-md overflow-hidden">
          <div className="bg-gray-50 dark:bg-gray-900 px-3 py-2 text-sm font-medium text-gray-900 dark:text-white flex items-center gap-2">
            <Layers className="h-4 w-4 text-gray-500" />
            {group.group_name}
            {group.is_repeating && (
              <span className="text-xs bg-blue-100 text-blue-800 dark:bg-blue-900/30 dark:text-blue-300 px-2 py-0.5 rounded">Repeating</span>
            )}
            <span className="text-xs text-gray-500 dark:text-gray-400">({group.fields?.length ?? 0} fields)</span>
          </div>
          {group.description && (
            <div className="px-3 py-1 text-xs text-gray-600 dark:text-gray-300 border-b border-gray-100 dark:border-gray-700">
              {group.description}
            </div>
          )}
          <div className="divide-y divide-gray-200 dark:divide-gray-700">
            {(group.fields ?? []).map((field) => (
              <FieldDetailRow key={field.id} field={field} editChecks={editChecks} />
            ))}
            {(group.fields ?? []).length === 0 && (
              <p className="px-3 py-2 text-sm text-yellow-600 dark:text-yellow-400">Item group has no fields.</p>
            )}
          </div>
        </div>
      ))}

      {ungrouped.length > 0 && (
        <div className="border border-gray-200 dark:border-gray-700 rounded-md overflow-hidden">
          <div className="bg-gray-50 dark:bg-gray-900 px-3 py-2 text-sm font-medium text-gray-900 dark:text-white">
            Ungrouped Fields ({ungrouped.length})
          </div>
          <div className="divide-y divide-gray-200 dark:divide-gray-700">
            {ungrouped.map((field) => (
              <FieldDetailRow key={field.id} field={field} editChecks={editChecks} />
            ))}
          </div>
        </div>
      )}
    </div>
  )
}

function formatInclusivity(flag: boolean | null | undefined, side: 'lower' | 'upper') {
  if (flag === true) return side === 'lower' ? 'inclusive' : 'inclusive'
  if (flag === false) return side === 'lower' ? 'exclusive' : 'exclusive'
  return 'unknown'
}

// Highlight numeric thresholds/units such as "≥ 18 years", "BMI 18.5-30 kg/m2", etc.
const THRESHOLD_RE = /(\b(?:≥|<=|≤|>=|>|<)?\s*\d+(?:\.\d+)?(?:\s*(?:-|–|to)\s*\d+(?:\.\d+)?)?\s*(?:mmHg|msec|ms|bpm|beats?\/min|breaths?\/min|°C|celsius|kg\/m2|kg\/m\^2|x\s*ULN|ULN|mg\/dL|mg\/dl|g\/dL|ng\/L|mL\/min\/1\.73\s*m2|cm|kg|lbs?|%|percent|years?|months?)\b)/gi

function HighlightedCriterion({ text }: { text: string }) {
  const parts: React.ReactNode[] = []
  let last = 0
  for (const m of text.matchAll(THRESHOLD_RE)) {
    const start = m.index ?? 0
    if (start > last) {
      parts.push(<span key={`t-${last}`}>{text.slice(last, start)}</span>)
    }
    parts.push(
      <mark key={`h-${start}`} className="rounded px-1 bg-yellow-100 text-yellow-900 dark:bg-yellow-900/40 dark:text-yellow-200 font-medium">
        {m[0]}
      </mark>
    )
    last = start + m[0].length
  }
  if (last < text.length) {
    parts.push(<span key={`t-${last}`}>{text.slice(last)}</span>)
  }
  return <>{parts}</>
}

function CriteriaList({ criteria, search }: { criteria: Criterion[]; search: string }) {
  const filtered = criteria.filter((c) =>
    c.criterion_text.toLowerCase().includes(search.toLowerCase())
  )
  if (filtered.length === 0) {
    return <p className="text-sm text-gray-500 dark:text-gray-400">No criteria found.</p>
  }
  return (
    <div className="space-y-2">
      {filtered.map((c) => (
        <div key={c.id} className="text-sm text-gray-900 dark:text-white leading-relaxed select-text">
          <span className="font-medium select-text">{c.criterion_number}. </span>
          <HighlightedCriterion text={c.criterion_text} />
        </div>
      ))}
    </div>
  )
}

function CriteriaTab({ study, search, onSearchChange }: { study: StudyDetail; search: string; onSearchChange: (v: string) => void }) {
  const inclusion = study.criteria.filter((c) => c.criterion_type === 'inclusion')
  const exclusion = study.criteria.filter((c) => c.criterion_type === 'exclusion')

  return (
    <div className="space-y-8">
      <div className="flex items-center justify-between">
        <h3 className="text-lg font-semibold text-gray-900 dark:text-white">Inclusion / Exclusion Criteria</h3>
        <input
          type="text"
          value={search}
          onChange={(e) => onSearchChange(e.target.value)}
          placeholder="Search criteria..."
          className="px-3 py-1.5 text-sm border rounded dark:bg-gray-800 dark:border-gray-600 dark:text-white"
        />
      </div>
      <div>
        <h3 className="text-md font-semibold text-gray-900 dark:text-white mb-3">Inclusion Criteria</h3>
        <CriteriaList criteria={inclusion} search={search} />
      </div>
      <div>
        <h3 className="text-md font-semibold text-gray-900 dark:text-white mb-3">Exclusion Criteria</h3>
        <CriteriaList criteria={exclusion} search={search} />
      </div>
    </div>
  )
}

export function FieldDetailRow({ field, editChecks }: { field: Field; editChecks: EditCheck[] }) {
  const missingLabel = !field.field_label?.trim()
  const missingPrompt = !field.prompt?.trim()
  const missingHelp = !field.help_text?.trim()
  const missingSource = field.evidence_status === 'missing' || !field.source_evidence?.trim()
  const codeList = field.code_list
  const fieldEditChecks = editChecks.filter((ec) => ec.affected_field_ids?.includes(field.id))
  const rangeChecks = Array.isArray(field.validation_rules)
    ? field.validation_rules
    : field.validation_rules && Object.keys(field.validation_rules).length > 0
      ? [field.validation_rules]
      : []
  const rangeMeta = (field.range_metadata || field.validation_rules?.protocol_range) as Record<string, any> | undefined
  const rangeStatus = rangeMeta?.range_status as string | undefined
  const rangeMissing = !rangeMeta || rangeStatus === 'not_specified_in_protocol'
  const evidenceInferred = field.evidence_status === 'inferred_from_parent'

  return (
    <div className="px-3 py-2 text-sm">
      <div className="flex items-center gap-2 flex-wrap">
        <span className="font-medium text-gray-900 dark:text-white">{field.field_name}</span>
        <span className="text-xs text-gray-500 dark:text-gray-400">{field.field_type}</span>
        {field.is_required && (
          <span className="text-xs bg-red-100 text-red-800 dark:bg-red-900/30 dark:text-red-300 px-2 py-0.5 rounded">Required</span>
        )}
        {field.sas_field_name && (
          <span className="text-xs text-gray-500 dark:text-gray-400">SAS: {field.sas_field_name}</span>
        )}
        {field.confidence !== null && field.confidence !== undefined && (
          <span className="text-xs text-gray-500 dark:text-gray-400">{(field.confidence * 100).toFixed(0)}% confident</span>
        )}
      </div>
      {(field.prompt || field.field_label) && (
        <p className="text-gray-700 dark:text-gray-300 mt-0.5">{field.prompt || field.field_label}</p>
      )}
      {(missingLabel || missingPrompt || missingHelp || missingSource) && (
        <p className="text-xs text-yellow-600 dark:text-yellow-400 mt-1 flex items-center gap-1">
          <AlertCircle className="h-3 w-3" />
          Missing: {[
            missingLabel && 'label',
            missingPrompt && 'prompt',
            missingHelp && 'help text',
            missingSource && 'source evidence',
          ].filter(Boolean).join(', ')}
        </p>
      )}
      {field.help_text && field.help_text !== field.prompt && (
        <p className="text-xs text-gray-500 dark:text-gray-400 mt-0.5">Help: {field.help_text}</p>
      )}
      {field.source_evidence && (
        <p className={`text-xs mt-0.5 ${evidenceInferred ? 'text-orange-600 dark:text-orange-400' : 'text-gray-500 dark:text-gray-400'}`}>
          Source{evidenceInferred ? ' (inferred)' : ''}: {field.source_evidence}
          {field.evidence_explanation && ` — ${field.evidence_explanation}`}
        </p>
      )}
      {rangeMeta && !rangeMissing && (
        <p className="text-xs text-gray-600 dark:text-gray-300 mt-0.5">
          <span className="font-medium">Range ({rangeMeta.item_name || field.field_name}):</span>{' '}
          {rangeMeta.lower_bound !== null && rangeMeta.lower_bound !== undefined && `${rangeMeta.lower_bound} ${formatInclusivity(rangeMeta.lower_inclusive, 'lower')}`}
          {rangeMeta.lower_bound !== null && rangeMeta.lower_bound !== undefined && rangeMeta.upper_bound !== null && rangeMeta.upper_bound !== undefined && ' — '}
          {rangeMeta.upper_bound !== null && rangeMeta.upper_bound !== undefined && `${rangeMeta.upper_bound} ${formatInclusivity(rangeMeta.upper_inclusive, 'upper')}`}
          {rangeMeta.unit && ` ${rangeMeta.unit}`}
          {rangeMeta.comparator_text && ` (${rangeMeta.comparator_text})`}
          {rangeMeta.confidence !== undefined && ` • ${(Number(rangeMeta.confidence) * 100).toFixed(0)}% confident`}
          {rangeMeta.source_span && (
            <span className="block text-gray-500 dark:text-gray-400 truncate" title={String(rangeMeta.source_span)}>
              Source: {String(rangeMeta.source_span)}
            </span>
          )}
        </p>
      )}
      {rangeMissing && rangeMeta?.essential && (
        <p className="text-xs text-yellow-600 dark:text-yellow-400 mt-0.5 flex items-center gap-1">
          <AlertCircle className="h-3 w-3" />
          Range not specified in protocol: no explicit {rangeMeta.item_name || field.field_name} range found.
        </p>
      )}
      {field.calculated_method && (
        <p className="text-xs text-purple-600 dark:text-purple-400 mt-0.5">Method: {field.calculated_method}</p>
      )}
      {field.conditional_visibility_expression && (
        <p className="text-xs text-blue-600 dark:text-blue-400 mt-0.5">Visibility: {field.conditional_visibility_expression}</p>
      )}
      {rangeChecks.length > 0 && (
        <p className="text-xs text-gray-500 dark:text-gray-400 mt-0.5">
          Range checks:{' '}
          {rangeChecks
            .map((rc: any) =>
              rc.comparator
                ? `${rc.comparator} ${(rc.check_values ?? []).join(', ')}`
                : JSON.stringify(rc)
            )
            .join('; ')}
        </p>
      )}
      {fieldEditChecks.length > 0 && (
        <div className="mt-1 text-xs text-gray-600 dark:text-gray-400">
          <span className="font-medium">Edit checks:</span>{' '}
          {fieldEditChecks.map((ec) => `${ec.check_name}: ${ec.expression}`).join('; ')}
        </div>
      )}
      {codeList && (
        <div className="mt-1 text-xs text-gray-600 dark:text-gray-400">
          <span className="font-medium">Code list {codeList.list_code || codeList.list_name}:</span>{' '}
          {codeList.items.map((item) => `${item.item_value}${item.item_label && item.item_label !== item.item_value ? `=${item.item_label}` : ''}`).join(', ')}
        </div>
      )}
    </div>
  )
}
